import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import random from 'lodash/random';
import Button from 'components/Button';
import styled from 'styled-components';
import { sizes } from 'style';
import { th } from 'style/theme';
import CustomizeAvatarModal from './components/CustomizeAvatarModal';
import Tabs from './components/Tabs';
import Preview from './components/Preview';
import Background from './components/Background';
import HeadShape from './components/HeadShape';
import Hairstyle from './components/Hairstyle';
import Eyebrows from './components/Eyebrows';
import Eyes from './components/Eyes';
import Beak from './components/Beak';
import Top from './components/Top';
import Eyewear from './components/Eyewear';
import { PARTS, COLORS } from './components/Avatar/consts';

const Wrapper = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 34px 300px 93px calc(100vh - 300px - 34px - 93px);
  grid-template-areas:
    'buttons'
    'preview'
    'tabs'
    'customizer';

  @media ${sizes.desktop} {
    grid-template-columns: 33% 67%;
    grid-template-rows: 97px calc(100% - 97px - 41px) 41px;
    grid-template-areas:
      'tabs tabs'
      'preview customizer'
      'preview buttons';
  }
`;

const Buttons = styled.div`
  grid-area: buttons;
  display: flex;
  justify-content: space-between;

  > button {
    :first-child {
      padding: 12px 0 0 25px;
      font-size: 16px;
    }

    :last-child {
      padding: 12px 25px 0 0;
      background: transparent;
      color: ${th('colors.black')};
      box-shadow: none;
    }
  }

  @media ${sizes.desktop} {
    justify-content: flex-end;
    padding-right: 6px;

    > button {
      :first-child {
        padding: 9px 25px;
        font-size: 14px;
      }

      :last-child {
        padding: 9px 35px;
        background: ${th('colors.black')};
        color: ${th('colors.white')};
        box-shadow: ${th('shadows.small')};
      }
    }
  }
`;

const CurrentStepContainer = styled.div`
  grid-area: customizer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: ${th('colors.blueBayoux')};
  width: 100%;
  overflow: scroll;

  @media ${sizes.desktop} {
    height: 90%;
    justify-content: space-between;
    flex-direction: row;
    padding: 0 20px;
    background-color: transparent;
  }
`;

const CustomizeAvatar = ({ onClose, isOpen, saveAvatar, avatar, firstName }) => {
  const [config, setConfig] = useState({
    background: '#FFE8D8',
    headShape: 0,
    headShapeColor: '#FFC2B9',
    eyes: 0,
    eyesColor: '#47507C',
    eyebrows: 1,
    eyebrowsColor: '#C75CC5',
    eyewear: 0,
    eyewearColor: 'null',
    top: 1,
    topColor: '#5D5CC7',
    hairstyle: 0,
    hairstyleColor: '#C75CC5',
    beak: 1,
    beakColor: '#EDCE68'
  });

  const [activeTab, setActiveTab] = useState('background');
  const [isRandomizing, setIsRandomizing] = useState(false);

  const renderCurrentStep = () => {
    switch (activeTab) {
      case 'background':
        return <Background config={config} updateConfig={setConfig} />;
      case 'head_shape':
        return <HeadShape config={config} updateConfig={setConfig} />;
      case 'hairstyle':
        return <Hairstyle config={config} updateConfig={setConfig} />;
      case 'eyebrows':
        return <Eyebrows config={config} updateConfig={setConfig} />;
      case 'eyes':
        return <Eyes config={config} updateConfig={setConfig} />;
      case 'beak':
        return <Beak config={config} updateConfig={setConfig} />;
      case 'top':
        return <Top config={config} updateConfig={setConfig} />;
      case 'eyewear':
        return <Eyewear config={config} updateConfig={setConfig} />;
      default:
        return <Background config={config} updateConfig={setConfig} />;
    }
  };

  const randomize = () => {
    setConfig({
      background: PARTS.BACKGROUND[random(0, PARTS.BACKGROUND.length - 1)],
      headShape: random(0, PARTS.HEAD_SHAPE().length - 1),
      headShapeColor: COLORS.HEAD_SHAPE[random(0, COLORS.HEAD_SHAPE.length - 1)],
      eyes: random(0, PARTS.EYES().length - 1),
      eyesColor: COLORS.EYES[random(0, COLORS.EYES.length - 1)],
      eyebrows: random(0, PARTS.EYEBROWS().length - 1),
      eyebrowsColor: COLORS.EYEBROWS[random(0, COLORS.EYEBROWS.length - 1)],
      eyewear: random(0, PARTS.EYEWEAR().length - 1),
      top: random(0, PARTS.TOP().length - 1),
      topColor: COLORS.TOP[random(0, COLORS.TOP.length - 1)],
      hairstyle: random(0, PARTS.HAIRSTYLE().length - 1),
      hairstyleColor: COLORS.HAIRSTYLE[random(0, COLORS.HAIRSTYLE.length - 1)],
      beak: random(0, PARTS.BEAK().length - 1),
      beakColor: COLORS.BEAK[random(0, COLORS.BEAK.length - 1)],
      eyewearColor: 'null'
    });
  };

  // eslint-disable-next-line no-promise-executor-return
  const timer = (ms) => new Promise((res) => setTimeout(res, ms));

  async function slotRandomize() {
    setIsRandomizing(true);
    for (let i = 0; i < 5; i++) {
      randomize();
      await timer(100); //eslint-disable-line
    }
    setIsRandomizing(false);
  }

  useEffect(() => {
    setActiveTab('background');

    if (!avatar) {
      randomize();
    } else {
      setConfig(avatar);
    }
  }, [avatar]);

  return (
    <CustomizeAvatarModal
      isOpen={isOpen}
      onClose={onClose}
      background={config.background}
    >
      <Wrapper>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <Preview
          config={config}
          randomize={slotRandomize}
          isRandomizing={isRandomizing}
          firstName={firstName}
        />
        <CurrentStepContainer>{renderCurrentStep()}</CurrentStepContainer>
        <Buttons>
          <Button variant="delete" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="dark" onClick={() => saveAvatar(config)}>
            Save
          </Button>
        </Buttons>
      </Wrapper>
    </CustomizeAvatarModal>
  );
};

CustomizeAvatar.propTypes = {
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  saveAvatar: PropTypes.func,
  firstName: PropTypes.string,
  avatar: PropTypes.object
};

export default CustomizeAvatar;
